// ─────────────────────────────────────────────────────────────────────────
// Glass-i — Supplier Master picker for purchase headers (Stage 6P supplier link).
//
// A searchable AntD Select bound to the Supplier Master list (active suppliers
// only). Picking a supplier hands the full row back via
// `onSelectSupplier(id, supplier)` so the calling header can snapshot
// supplier_id + name + gstin + state-code onto the document. Supplier
// payables key strictly on supplier_id, so linking the master supplier here
// is what lets a UI-created PO / Bill surface for bill-wise payment.
//
// Picking is OPTIONAL — clearing the select reverts the header to its
// existing free-text supplier name (the prior behaviour is preserved). When
// a master supplier is chosen the free-text fields are auto-filled and the
// stable supplier_id rides through PO → GRN → Bill → Payment.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { supplierApi } from '../masters/suppliers/supplierApi';

// Normalise the list envelope to a plain array of supplier rows.
function toRows(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.rows)) return res.rows;
  return [];
}

// The 2-char GST state code lives on either gstStateCode (registered) or
// stateCode (address). Prefer the GST one.
function supplierStateCode(s) {
  if (!s) return '';
  return s.gstStateCode || s.stateCode || s.state_code || '';
}

export default function SupplierMasterSelect({
  value,
  label,
  disabled = false,
  size = 'middle',
  placeholder = 'Pick supplier from master…',
  onSelectSupplier,
  onClear
}) {
  // Seed with the already-linked supplier so its label renders before the
  // first search (edit / reload case).
  const [options, setOptions] = useState(() =>
    value && label ? [{ value, label, supplier: null }] : []);
  const [fetching, setFetching] = useState(false);
  const seq = useRef(0);

  // Keep a labelled option in sync with the linked supplier. On edit / reload
  // the PO is fetched asynchronously, so `value` (supplier id) and `label`
  // (supplier name) arrive AFTER mount — the useState seed above only catches
  // the synchronous case. Without this, the Select holds a value with no
  // matching option and AntD renders the raw supplier id (a bare UUID).
  useEffect(() => {
    if (!value) return undefined;
    // Prepend a labelled option for this id unless one already exists with a
    // real (non-id) label. Never treat the id itself as a valid label.
    const ensure = (lbl, supplier) => {
      const text = String(lbl || '').trim();
      if (!text || text === value) return;
      setOptions((prev) =>
        prev.some((o) => o.value === value && o.label && o.label !== value)
          ? prev
          : [{ value, label: text, supplier: supplier || null },
             ...prev.filter((o) => o.value !== value)]);
    };

    if (label) { ensure(label, null); return undefined; }

    // No label handed down (legacy / imported PO with an empty supplier_name)
    // — resolve the supplier by id so a name shows instead of the bare UUID.
    let cancelled = false;
    supplierApi.get(value).then((s) => {
      if (cancelled || !s) return;
      const nm = s.supplierName || s.supplier_name || '';
      const code = s.supplierCode || s.supplier_code || '';
      ensure(`${code}${code && nm ? ' — ' : ''}${nm}`, s);
    }).catch(() => { /* leave the field as-is on lookup failure */ });
    return () => { cancelled = true; };
  }, [value, label]);

  const search = useCallback(async (q) => {
    const mine = ++seq.current;
    setFetching(true);
    try {
      const res = await supplierApi.list({
        search: q || '',
        page: 1,
        pageSize: 20,
        filters: [{ field: 'status', value: 'active', op: 'equals' }]
      });
      if (mine !== seq.current) return; // a newer search superseded this one
      setOptions(toRows(res).map((s) => ({
        value: s.id,
        label: `${s.supplierCode || s.supplier_code || ''} — ${s.supplierName || s.supplier_name || ''}`,
        supplier: s
      })));
    } catch (_e) {
      if (mine === seq.current) setOptions([]);
    } finally {
      if (mine === seq.current) setFetching(false);
    }
  }, []);

  return (
    <Select
      showSearch
      allowClear
      size={size}
      placeholder={placeholder}
      style={{ width: '100%' }}
      value={value || undefined}
      disabled={disabled}
      filterOption={false}
      onSearch={search}
      onFocus={() => { if (options.length === 0) search(''); }}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={options}
      onClear={() => onClear && onClear()}
      onChange={(val, opt) => {
        if (!val) { onClear && onClear(); return; }
        const s = (opt && opt.supplier) || null;
        if (onSelectSupplier) {
          onSelectSupplier(val, s
            ? {
                supplierName:  s.supplierName || s.supplier_name || '',
                supplierGstin: s.gstin || '',
                supplierStateCode: supplierStateCode(s),
                raw: s
              }
            : null);
        }
      }}
    />
  );
}
